import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { MessageSquare, Send } from 'lucide-react'

import { chatService, type ChatConversation, type ChatMessage } from '../../services/chat.service'
import { Button } from '../../components/ui/button'
import { useLocale } from '../../hooks/use-locale'
import { cn } from '../../lib/utils'

/** Poll while chat is open – sufficient for low-traffic internal admin chat (no WebSocket infra). */
const CHAT_POLL_MS = 3_000

type ChatPanelProps = {
  employeeUserId: string
  employeeName?: string
  conversationId?: string
  onConversationCreated?: (conversation: ChatConversation) => void
  className?: string
}

export function ChatPanel({
  employeeUserId,
  employeeName,
  conversationId: initialConversationId,
  onConversationCreated,
  className,
}: ChatPanelProps) {
  const { t } = useTranslation()
  const { formatDateTime } = useLocale()
  const queryClient = useQueryClient()
  const scrollRef = useRef<HTMLDivElement>(null)
  const isNearBottomRef = useRef(true)

  const [conversationId, setConversationId] = useState(initialConversationId || '')
  const [draft, setDraft] = useState('')

  useEffect(() => {
    if (initialConversationId) setConversationId(initialConversationId)
  }, [initialConversationId])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (conversationId || !employeeUserId) return
      try {
        const conv = await chatService.getOrCreateConversation(employeeUserId)
        if (!cancelled) {
          setConversationId(conv.id)
          onConversationCreated?.(conv)
        }
      } catch {
        // non-blocking
      }
    })()
    return () => {
      cancelled = true
    }
  }, [conversationId, employeeUserId, onConversationCreated])

  const { data: templates = [] } = useQuery({
    queryKey: ['chat-templates'],
    queryFn: () => chatService.getTemplates(),
  })

  const ensureConversation = async () => {
    if (conversationId) return conversationId
    const conv = await chatService.getOrCreateConversation(employeeUserId)
    setConversationId(conv.id)
    onConversationCreated?.(conv)
    return conv.id
  }

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ['chat-messages', conversationId],
    queryFn: () => chatService.getMessages(conversationId),
    enabled: !!conversationId,
    refetchInterval: conversationId ? CHAT_POLL_MS : false,
    refetchIntervalInBackground: false,
  })

  useEffect(() => {
    if (!conversationId || isLoading) return
    chatService
      .markConversationRead(conversationId)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['chat-unread-count'] })
      })
      .catch(() => {})
  }, [conversationId, isLoading, messages.length, queryClient])

  const handleMessagesScroll = () => {
    const el = scrollRef.current
    if (!el) return
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  useEffect(() => {
    if (!isNearBottomRef.current) return
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages])

  const sendMutation = useMutation({
    mutationFn: async (message: string) => {
      await ensureConversation()
      return chatService.sendToEmployee(employeeUserId, message)
    },
    onSuccess: msg => {
      setDraft('')
      if (!conversationId) setConversationId(msg.conversationId)
      isNearBottomRef.current = true
      queryClient.invalidateQueries({ queryKey: ['chat-messages'] })
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] })
      queryClient.invalidateQueries({ queryKey: ['chat-unread-count'] })
    },
  })

  const templateMutation = useMutation({
    mutationFn: async (templateId: string) => {
      await ensureConversation()
      return chatService.sendTemplate(employeeUserId, templateId)
    },
    onSuccess: msg => {
      if (!conversationId) setConversationId(msg.conversationId)
      queryClient.invalidateQueries({ queryKey: ['chat-messages'] })
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] })
      queryClient.invalidateQueries({ queryKey: ['chat-unread-count'] })
    },
  })

  const handleSend = () => {
    const text = draft.trim()
    if (!text || sendMutation.isPending) return
    sendMutation.mutate(text)
  }

  return (
    <div className={cn('flex flex-col rounded-lg border border-neutral-200 bg-white', className)}>
      <div className='border-b border-neutral-100 px-4 py-3'>
        <div className='flex items-center gap-2'>
          <MessageSquare className='w-4 h-4 text-primary-600' />
          <h3 className='text-sm font-semibold text-neutral-900'>
            {employeeName || t('chat.title')}
          </h3>
        </div>
      </div>

      {templates.length > 0 && (
        <div className='flex flex-wrap gap-2 border-b border-neutral-100 px-4 py-3'>
          {templates.map(template => (
            <button
              key={template.id}
              type='button'
              disabled={templateMutation.isPending}
              onClick={() => templateMutation.mutate(template.id)}
              className='rounded-md border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-primary-50 hover:border-primary-200 hover:text-primary-700 transition-colors'
            >
              {template.label}
            </button>
          ))}
        </div>
      )}

      <div
        ref={scrollRef}
        onScroll={handleMessagesScroll}
        className='flex-1 min-h-[280px] max-h-[420px] overflow-y-auto p-4 space-y-2 bg-neutral-50/50'
      >
        {isLoading && !conversationId ? (
          <p className='text-sm text-neutral-500 text-center py-8'>{t('common.loading')}</p>
        ) : messages.length === 0 ? (
          <p className='text-sm text-neutral-500 text-center py-8'>{t('chat.empty')}</p>
        ) : (
          messages.map((msg: ChatMessage) => (
            <MessageBubble key={msg.id} message={msg} formatDateTime={formatDateTime} t={t} />
          ))
        )}
      </div>

      <div className='border-t border-neutral-100 p-3 flex gap-2'>
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder={t('chat.placeholder')}
          rows={2}
          className='flex-1 resize-none rounded-md border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200'
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSend()
            }
          }}
        />
        <Button
          size='sm'
          onClick={handleSend}
          disabled={!draft.trim() || sendMutation.isPending}
          className='self-end'
        >
          <Send className='w-4 h-4' />
        </Button>
      </div>
    </div>
  )
}

function MessageBubble({
  message,
  formatDateTime,
  t,
}: {
  message: ChatMessage
  formatDateTime: (value: string) => string
  t: (key: string) => string
}) {
  const isSystem = message.messageType === 'SYSTEM'
  const isMine = message.isMine

  return (
    <div
      className={cn('flex', isSystem ? 'justify-center' : isMine ? 'justify-end' : 'justify-start')}
    >
      <div
        className={cn(
          'max-w-[88%] rounded-lg px-3 py-2 text-sm',
          isSystem
            ? 'bg-primary-50 border border-primary-100 text-neutral-900'
            : isMine
              ? 'bg-primary-600 text-white'
              : 'bg-white border border-neutral-200 text-neutral-900'
        )}
      >
        {isSystem ? (
          <div className='flex items-center gap-1 text-xs font-semibold text-primary-700 mb-1'>
            <MessageSquare className='w-3 h-3' />
            {t('chat.system')}
          </div>
        ) : !isMine ? (
          <div className='text-xs font-semibold text-primary-700 mb-1'>
            {message.senderName || t('chat.employee')}
          </div>
        ) : null}
        <p className='whitespace-pre-wrap'>{message.body}</p>
        <p
          className={cn(
            'text-[10px] mt-1 text-right',
            isMine && !isSystem ? 'text-primary-100' : 'text-neutral-400'
          )}
        >
          {formatDateTime(message.createdAt)}
        </p>
      </div>
    </div>
  )
}

export function ChatConversationList({
  selectedEmployeeId,
  onSelect,
}: {
  selectedEmployeeId?: string
  onSelect: (conv: ChatConversation) => void
}) {
  const { t } = useTranslation()
  const { formatDateTime } = useLocale()

  const { data: conversations = [], isLoading } = useQuery({
    queryKey: ['chat-conversations'],
    queryFn: () => chatService.listConversations(),
    refetchInterval: CHAT_POLL_MS,
    refetchIntervalInBackground: false,
  })

  if (isLoading) {
    return <p className='text-sm text-neutral-500 p-4'>{t('common.loading')}</p>
  }

  return (
    <div className='divide-y divide-neutral-100'>
      {conversations.map(conv => (
        <button
          key={conv.employeeUserId}
          type='button'
          onClick={() => onSelect(conv)}
          className={cn(
            'w-full text-left px-4 py-3 hover:bg-neutral-50 transition-colors',
            selectedEmployeeId === conv.employeeUserId && 'bg-primary-50'
          )}
        >
          <div className='flex items-center justify-between gap-2'>
            <span className='font-medium text-sm text-neutral-900 truncate'>
              {conv.employeeName}
            </span>
            <div className='flex items-center gap-2 flex-shrink-0'>
              {(conv.unreadCount ?? 0) > 0 && (
                <span className='w-2 h-2 rounded-full bg-error-500' />
              )}
              {conv.lastMessageAt && (
                <span className='text-[10px] text-neutral-400'>
                  {formatDateTime(conv.lastMessageAt)}
                </span>
              )}
            </div>
          </div>
          <p className='text-xs text-neutral-500 truncate mt-0.5'>
            {conv.lastMessage || t('chat.noMessagesYet')}
          </p>
        </button>
      ))}
    </div>
  )
}

export function ChatEmployeeLink({
  employeeUserId,
  children,
}: {
  employeeUserId: string
  children: React.ReactNode
}) {
  return (
    <Link to={`/chat?employee=${employeeUserId}`} className='text-primary-600 hover:underline'>
      {children}
    </Link>
  )
}
